import {
  AssistantCompletionRequest,
  AssistantCompletionResponse,
  AssistantContentPart,
  AssistantMessage,
  AssistantToolCallRequest,
  AssistantToolDefinition,
} from '@paragon/contract-library';

import { Plan, PlanDecision } from './assistantStore';
import { parsePreviewRequest, PREVIEW_SCHEMA, PreviewRequest } from './planPreviewRequest';
import { ToolOutcome } from './tools/runTool';

export const TOOL_CALL_CAP_PER_TURN = 100;

/**
 * How many times in a Turn the same call may return the same result before it's refused, so a model that keeps
 * retrying an edit that isn't taking stops and says so instead of looping.
 */
export const REPEATED_CALL_LIMIT = 3;

export const PROPOSE_PLAN_TOOL: AssistantToolDefinition = {
  name: 'propose_plan',
  description:
    'States what you will change in this turn and waits for the designer to approve it. Call it before your first ' +
    'change to the project in a turn. Include `preview` for anything you add, remove, move, or reshape, loads ' +
    'included, so the designer sees it on the view. Returns whether the designer approved, with any feedback.',
  inputSchema: {
    type: 'object',
    properties: {
      summary: { type: 'string', description: 'One or two sentences on what you will do.' },
      steps: { type: 'array', items: { type: 'string' }, description: 'The changes, in order.' },
      preview: PREVIEW_SCHEMA,
    },
    required: ['summary', 'steps'],
  },
};

export interface TurnDependencies {
  readonly conversationGuid: string;
  readonly turnGuid: string;
  readonly tools: readonly AssistantToolDefinition[];
  readonly complete: (request: AssistantCompletionRequest) => Promise<AssistantCompletionResponse>;
  readonly runTool: (name: string, input: unknown, planApproved: boolean) => Promise<ToolOutcome>;
  readonly proposePlan: (plan: Plan, preview: PreviewRequest | null) => Promise<PlanDecision>;
  readonly getSnapshot: () => object;

  /**
   * Checked after every step. Stop takes effect after the step that is running.
   */
  readonly shouldStop: () => boolean;

  readonly onMessages: (messages: readonly AssistantMessage[]) => void;
  readonly onToolStarted?: (call: AssistantToolCallRequest) => void;
  readonly onToolFinished?: (call: AssistantToolCallRequest, outcome: ToolOutcome) => void;
}

export type TurnEnd = 'replied' | 'stopped';

/**
 * Runs one Turn: sends the conversation to the model, runs the tools it asks for in the page, and repeats until it
 * replies to the designer. New messages are reported through `onMessages` as they're added.
 */
export async function runTurn(
  history: readonly AssistantMessage[],
  userContent: string | readonly AssistantContentPart[],
  dependencies: TurnDependencies,
): Promise<TurnEnd> {
  const messages: AssistantMessage[] = [...history];
  const add = (...newMessages: AssistantMessage[]) => {
    messages.push(...newMessages);
    dependencies.onMessages(newMessages);
  };

  add({ role: 'user', content: userContent });

  let planApproved = false;
  let toolCallsThisTurn = 0;
  const repeats = new Map<string, { result: string; count: number }>();

  for (;;) {
    const response = await dependencies.complete({
      conversationGuid: dependencies.conversationGuid,
      turnGuid: dependencies.turnGuid,
      messages: [...messages],
      tools: [PROPOSE_PLAN_TOOL, ...dependencies.tools],
      snapshot: dependencies.getSnapshot(),
    });
    add(response.message);

    const toolCalls = response.message.tool_calls ?? [];
    if (toolCalls.length === 0) return 'replied';

    const toolMessages: AssistantMessage[] = [];
    const attachments: AssistantMessage[] = [];
    let stopped = false;

    // Every call gets an answer, even after Stop, since the model expects one for each.
    for (const call of toolCalls) {
      if (stopped || dependencies.shouldStop()) {
        stopped = true;
        toolMessages.push(toolMessage(call, 'Not run: the designer stopped the Assistant.'));
        continue;
      }

      if (toolCallsThisTurn >= TOOL_CALL_CAP_PER_TURN) {
        toolMessages.push(toolMessage(call, `Not run: the limit of ${TOOL_CALL_CAP_PER_TURN} tool calls per turn.`));
        continue;
      }
      toolCallsThisTurn++;

      const input = parseArguments(call.function.arguments);
      if (input === undefined) {
        toolMessages.push(toolMessage(call, 'Error: the arguments were not valid JSON.'));
        continue;
      }

      if (call.function.name === PROPOSE_PLAN_TOOL.name) {
        const decision = await dependencies.proposePlan(
          toPlan(input),
          // Models sometimes put the preview's fields beside the summary instead of inside `preview`.
          parsePreviewRequest((input as { preview?: unknown } | null)?.preview ?? input),
        );
        planApproved = decision.approved;
        toolMessages.push(toolMessage(call, JSON.stringify(decision)));
        continue;
      }

      const callKey = `${call.function.name} ${JSON.stringify(input)}`;
      if ((repeats.get(callKey)?.count ?? 0) >= REPEATED_CALL_LIMIT) {
        toolMessages.push(
          toolMessage(
            call,
            `Not run: this exact call already returned the same result ${REPEATED_CALL_LIMIT} times this turn, so ` +
              'repeating it won’t change anything. Stop, and tell the designer what you were trying to reach, what ' +
              'the app gave instead, and what you could try next.',
          ),
        );
        continue;
      }

      dependencies.onToolStarted?.(call);
      const outcome = await dependencies.runTool(call.function.name, input, planApproved);
      dependencies.onToolFinished?.(call, outcome);

      const content = outcome.ok ? JSON.stringify(outcome.result.summary) : `Error: ${outcome.error}`;
      // Screenshots are exempt: the same call is expected to show something new each time.
      if (!(outcome.ok && outcome.result.image)) {
        const previous = repeats.get(callKey);
        repeats.set(callKey, { result: content, count: previous?.result === content ? previous.count + 1 : 1 });
      }
      toolMessages.push(toolMessage(call, content));
      if (!outcome.ok) continue;

      if (outcome.result.image)
        attachments.push({
          role: 'user',
          paragon_attachment: true,
          content: [
            { type: 'text', text: `The screenshot from ${call.function.name} (${call.id}).` },
            { type: 'image_url', image_url: { url: outcome.result.image } },
          ],
        });
    }

    add(...toolMessages, ...attachments);

    if (stopped || dependencies.shouldStop()) return 'stopped';
  }
}

function toolMessage(call: AssistantToolCallRequest, content: string): AssistantMessage {
  return { role: 'tool', tool_call_id: call.id, content };
}

function parseArguments(argumentsJson: string): unknown {
  if (argumentsJson.trim() === '') return {};
  try {
    return JSON.parse(argumentsJson);
  } catch {
    return undefined;
  }
}

function toPlan(input: unknown): Plan {
  const { summary, steps } = (input ?? {}) as { summary?: unknown; steps?: unknown };
  return {
    summary: typeof summary === 'string' ? summary : '',
    steps: Array.isArray(steps) ? steps.filter((step): step is string => typeof step === 'string') : [],
  };
}
